import type { CURRENTY_ENUM } from "@/types/currency";
import { CURRENTY_CODE_MAP, DATE_TYPE_MAP } from "./constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { formatSecondToDate } from "@/utils/time";
import { getCurrency } from "@/utils/currency";
import { formatNumberWithCommas, toNum } from "@/libs/decimal";

// 扩展 dayjs 以支持 UTC 和时区
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 截断小数，不进行四舍五入
 * @param value 数值
 * @param decimals 保留的小数位数（默认 2）
 * @returns 截断后的数值
 * 
 * @example
 * truncateDecimals(123.456, 2)  // 123.45
 * truncateDecimals(123.999, 2)  // 123.99 (不会变成 124.00)
 * truncateDecimals(123.4, 2)    // 123.40
 */
export const truncateDecimals = (value: number, decimals: number = 2): string => {
  if (value > 0 && value < 0.01) return '0.01';
  const multiplier = Math.pow(10, decimals);
  const truncated = Math.floor(value * multiplier) / multiplier;
  // 去掉多余的0和点，如12.00->12，12.10->12.1，12.01->12.01
  return truncated.toFixed(decimals)
    .replace(/\.?0+$/, ''); // 移除结尾.0/.00等
};

interface renderCurrencyParams {
  currency: CURRENTY_ENUM,
  amount: number,
  truncate?: boolean,
  showCurrency?: boolean,
  currencySeperator?: string;
}
/**
 * 格式化货币显示
 * @param currency 货币类型
 * @param amount 金额
 * @param truncate 是否截断小数（默认 true）
 * @param showCurrency 是否展示货币符号（默认 true）
 * @returns 格式化后的货币字符串
 */
export const renderCurrency = ({ currency, amount, truncate = true, showCurrency = true, currencySeperator = '' }: renderCurrencyParams) => {
  if (!currency || amount < 0) {
    return '';
  }

  const prefix = amount > 0 && amount < 0.01 ? '<' : '';
  const finalAmount = truncate ? truncateDecimals(amount, 2) : amount;
  const currencyCode = CURRENTY_CODE_MAP[currency];
  return prefix + (showCurrency ? `${getCurrency(currencyCode)}${currencySeperator}` : '') + finalAmount;
};

/**
 * 格式化货币显示
 * @param currency 货币类型
 * @param amount 金额
 * @param truncate 是否截断小数（默认 true）
 * @param showCurrency 是否展示货币符号（默认 true）
 * @returns 格式化后的货币字符串
 */
export const formatCurrencyWithCommas = ({ currency, amount, truncate = true, showCurrency = true, locale }: { currency: CURRENTY_ENUM, amount: number, truncate?: boolean, showCurrency?: boolean, locale: string; }) => {
  if (!currency || amount < 0) {
    return '';
  }

  const prefix = amount > 0 && amount < 0.01 ? '<' : '';
  const finalAmount = truncate ? truncateDecimals(amount, 2) : amount;
  const currencyCode = CURRENTY_CODE_MAP[currency];
  return prefix + (showCurrency ? getCurrency(currencyCode) : '') + formatNumberWithCommas(toNum(finalAmount), locale, 2, false);
};

/**
 * 获取时间范围（Unix 时间戳）
 * 
 * @param dateType 日期类型
 * @param targetTimezone 目标时区（可选）
 *   - 'UTC': 使用 UTC 时区（推荐用于后端 API）
 *   - 'Asia/Kolkata': 印度时区（UTC+5:30）
 *   - undefined: 使用用户本地时区
 * @returns { start_date: number, end_date: number } Unix 时间戳（秒）
 * 
 * @example
 * // 使用 UTC 时区（推荐）
 * getTimeRange(DATE_TYPE_MAP.TODAY, 'UTC')
 * 
 * // 使用印度时区
 * getTimeRange(DATE_TYPE_MAP.TODAY, 'Asia/Kolkata')
 * 
 * // 使用用户本地时区
 * getTimeRange(DATE_TYPE_MAP.TODAY)
 */
export const getTimeRange = (dateType: DATE_TYPE_MAP, targetTimezone?: string, template = 'YYYYMMDD') => {
  // 根据是否指定时区创建 dayjs 实例
  const getDayjs = () => {
    if (targetTimezone === 'UTC') {
      return dayjs.utc();
    } else if (targetTimezone) {
      return dayjs().tz(targetTimezone);
    } else {
      return dayjs(); // 使用本地时区
    }
  };

  switch (dateType) {
    case DATE_TYPE_MAP.TODAY:
      return {
        start_date: getDayjs().startOf('day').format(template),
        end_date: getDayjs().endOf('day').format(template)
      };
    case DATE_TYPE_MAP.YESTERDAY:
      return {
        start_date: getDayjs().subtract(1, 'day').startOf('day').format(template),
        end_date: getDayjs().subtract(1, 'day').endOf('day').format(template)
      };
    case DATE_TYPE_MAP.THREE_DAYS:
      return {
        start_date: getDayjs().subtract(2, 'day').startOf('day').format(template),
        end_date: getDayjs().endOf('day').format(template)
      };
    default:
      return {};
  }
};

export const getCumnlativeDateRange = (dateType: DATE_TYPE_MAP, start_date: number, end_date?: number, t?: any) => {
  switch (dateType) {
    case DATE_TYPE_MAP.TODAY:
      return t?.('todayTotal') || 'Today\'s cumnlative';
    case DATE_TYPE_MAP.YESTERDAY:
      return t?.('yesterdayTotal') || 'Yesterday\'s cumnlative';
    case DATE_TYPE_MAP.THREE_DAYS:
      return t?.('last3DaysTotal') || 'Last 3d\'s cumnlative';
    case DATE_TYPE_MAP.DATE_RANGE:
      const isSameDay = end_date ? dayjs(start_date).isSame(dayjs(end_date), 'day') : true;
      return `${formatSecondToDate(start_date / 1000, 'DD/MM/YYYY')}${isSameDay || !end_date ? '' : ` - ${formatSecondToDate(end_date / 1000, 'DD/MM/YYYY')}`}`;
    default:
      return '';
  }
};
